import React, { useState, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw, Clock } from 'lucide-react';

interface TimerProps {
  initialMinutes?: number;
  className?: string;
}

export function Timer({ initialMinutes = 30, className = '' }: TimerProps) {
  const [timeInSeconds, setTimeInSeconds] = useState(initialMinutes * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [inputMinutes, setInputMinutes] = useState(initialMinutes.toString());
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRunning) {
      interval = setInterval(() => {
        setTimeInSeconds(prev => prev - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isRunning]);

  const formatTime = useCallback((seconds: number) => {
    const isNegative = seconds < 0;
    const absSeconds = Math.abs(seconds);
    const minutes = Math.floor(absSeconds / 60);
    const remainingSeconds = absSeconds % 60;
    const formatted = `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    return isNegative ? `-${formatted}` : formatted;
  }, []);

  const handleReset = () => {
    setIsRunning(false);
    setTimeInSeconds(parseInt(inputMinutes) * 60);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && parseInt(value || '0') <= 999) {
      setInputMinutes(value);
    }
  };

  const handleTimeSubmit = () => {
    const minutes = Math.max(1, parseInt(inputMinutes || '30'));
    setInputMinutes(minutes.toString());
    setTimeInSeconds(minutes * 60);
    setIsEditing(false);
  };

  const getTimerColor = () => {
    if (timeInSeconds <= 0) return 'text-crimson';
    if (timeInSeconds <= 300) return 'text-gold'; // 5 minutes
    return 'text-emerald dark:text-emerald';
  };

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <div className="flex items-center gap-2">
        <Clock className="h-5 w-5 text-slate" />
        {isEditing ? (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={inputMinutes}
              onChange={handleTimeChange}
              onKeyDown={(e) => e.key === 'Enter' && handleTimeSubmit()}
              className="w-16 px-2 py-1 border border-sand rounded-lg text-center"
              autoFocus
            />
            <span className="text-slate">min</span>
            <button
              onClick={handleTimeSubmit}
              className="px-2 py-1 bg-electric text-white rounded-lg text-sm"
            >
              Set
            </button>
          </div>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className={`text-xl font-mono font-bold ${getTimerColor()} min-w-[5rem] text-center`}
            title="Click to edit time"
          >
            {formatTime(timeInSeconds)}
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setIsRunning(!isRunning)}
          className={`p-2 rounded-lg ${
            isRunning 
              ? 'bg-crimson/10 text-crimson hover:bg-crimson/20'
              : 'bg-emerald/10 text-emerald hover:bg-emerald/20'
          }`}
          title={isRunning ? 'Pause' : 'Start'}
        >
          {isRunning ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="h-5 w-5" />
          )}
        </button>
        <button
          onClick={handleReset}
          className="p-2 rounded-lg bg-sand/10 text-slate hover:bg-sand/20"
          title="Reset"
        >
          <RotateCcw className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}